import moment from 'moment';
import { getData } from 'country-list';

export const userVariables =
  '{client_fname}, {client_lname}, {client_full_name}, {client_preferred_name}, {client_email}, {client_phone_number}, {assigned_firm_user_name}, {firm_name}';
export const entityVariables =
  '{entity_name}, {entity_type}, {owner_full_names}, {owner_salutation_names}, {salutation_first_name}';
export const questionnaireVariables =
  '{signer_names}, {questionnaire_name}, {questionnaire_year}, {due_date}, {number_of_items_left}, {by_due_date}, {required_documents}';

export const customizedWordsVariables = {
  userVariables: {
    '{assigned_firm_user_name}': {
      definition: 'The name of the firm user assigned to the client',
      example: 'Jane Doe',
    },
    '{client_email}': {
      definition: 'The client’s email address',
      example: 'john.smith@example.com',
    },
    '{client_fname}': {
      definition: 'The first name of the client',
      example: 'John',
    },
    '{client_full_name}': {
      definition: 'The full name of the client',
      example: 'John Smith',
    },
    '{client_lname}': {
      definition: 'The last name of the client',
      example: 'Smith',
    },
    '{client_phone_number}': {
      definition: 'The client’s phone number',
      example: '1234567890',
    },
    '{client_preferred_name}': {
      definition: 'The preferred name of the client',
      example: 'Johnny',
    },
    '{firm_name}': {
      definition: 'The name of the firm',
      example: 'ABC Firm',
    },
  },
  entityVariables: {
    '{entity_name}': {
      definition: 'The name of the entity',
      example: 'Smith Enterprises LLC',
    },
    '{entity_type}': {
      definition: 'The type of entity (Individual, Trust, etc)',
      example: 'Individual',
    },
    '{for_wording}': {
      definition:
        "When the questionnaire is for a different entity than the client's personal individual entity, it will use that entity's name",
      example: 'for ABC Corp',
    },
    '{owner_full_names}': {
      definition: 'A list of all the entity’s owners',
      example: 'John Smith and Jane Smith',
    },
    '{salutation_first_name}': {
      definition:
        'The first name of the primary owner and the first name of the spouse',
      example: 'John and Jane',
    },
  },
  questionnaireVariables: {
    '{by_due_date}': {
      definition: 'The action that needs to be done by the due date',
      example: 'by March 15, 2024',
    },
    '{due_date}': {
      definition: 'The due date of the questionnaire',
      example: 'March 15, 2024',
    },
    '{number_of_items_left}': {
      definition: 'The number of items remaining to complete',
      example: '5',
    },
    '{questionnaire_name}': {
      definition: 'The name of the questionnaire',
      example: 'Simple Tax Filing',
    },
    '{questionnaire_year}': {
      definition: 'The year of the questionnaire',
      example: '2024',
    },
    '{required_documents}': {
      definition:
        'A list of required documents (each will appear on its own line)',
      example: '•Statement of Dissolution •Updated Operating Agreement',
    },
    '{signer_names}': {
      definition: 'A list of all the signers',
      example: 'John Smith and Jane Smith',
    },
  },
};

export const preferredContactMethods = [
  {
    label: 'Email & Text',
    value: 'sms',
  },
  {
    label: 'Email',
    value: 'email',
  },
];

export const sectionTypes = [
  {
    id: 0,
    key: 'custom',
    label: 'Custom',
    defaultInstruction: 'Please complete the following.',
  },
  {
    id: 2,
    key: 'agreement_signature',
    label: 'Agreement Signature Request',
    defaultInstruction: 'Please e-sign the letter with your full name.',
  },
  {
    id: 3,
    key: 'questionnaire',
    label: 'Questionnaire',
    defaultInstruction: 'Please complete all items.',
  },
  {
    id: 4,
    key: 'review_and_submit',
    label: 'Review & Submit',
    defaultInstruction:
      'Please review your answers for all sections and submit your answers.',
  },
  {
    id: 6,
    key: 'pdf_review',
    label: 'PDF Review Request',
    defaultInstruction:
      'Please read the document and mark that you have thoroughly read it.',
    hidden: true,
  },
  {
    id: 7,
    key: 'payment_request',
    label: 'Payment Request',
    defaultInstruction: 'Please use the link below to pay the fee.',
    hidden: true,
  },
  {
    id: 8,
    key: 'download_document',
    label: 'Download Document',
    defaultInstruction: 'Please download the file below.',
    hidden: true,
  },
  {
    id: 9,
    key: 'pdf_signature',
    label: 'PDF Signature Request',
    defaultInstruction: 'Please sign on the signature box below.',
    hidden: true,
  },
  {
    id: 10,
    key: 'document_checklist',
    label: 'Document Checklist',
    defaultInstruction: 'Use this section to upload the requested documents.',
  },
  {
    id: 11,
    key: 'delivery',
    label: 'Tax Return Delivery',
    locked: true,
    defaultInstruction: 'Please sign form 8879 and download your tax return.',
  },
];

export const priorYearItemsImportMethods = [
  {
    name: 'ATX Tax Organizer',
    value: 'atx_tax_organizer',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'UltraTax Input Screens',
    value: 'ultratax_input_screens',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'Lacerte Tax Organizer',
    value: 'lacerte_tax_organizer',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'Drake Checklist',
    value: 'drake_checklist',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'ProSeries Tax Organizer',
    value: 'proseries_tax_organizer',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'ProSystem FX Tax Organizer',
    value: 'prosystem_fx_tax_organizer',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'ProConnect Organizer',
    value: 'proconnect_organizer',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'Axcess Tax Organizer',
    value: 'axcess_tax_organizer',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'TaxCycle Checklist',
    value: 'tax_cycle_checklist',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'ProFile Return',
    value: 'profile_return',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'TaxPrep Return (CSV)',
    value: 'taxprep_return',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'GoSystem Tax RS Organizer',
    value: 'go_system_tax_rs_organizer',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'TaxAct Organizer',
    value: 'tax_act_organizer',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'Tax Return',
    value: 'tax_return_for_rollover',
    supported: true,
    bulk_supported: true,
  },
  {
    name: 'Other',
    value: 'other_organizer',
    supported: true,
    bulk_supported: true,
  },
];

export const customSections = [
  {
    name: 'Rental Activity (Schedule E)',
    otherNames: [
      'Rental Activity',
      'Rents and Royalties',
      'Rental Activity (賃貸活動)',
      'Rental Property - Single',
      'Rental Property - Joint',
    ],
    form: 'Schedule E',
    addName: 'Property',
    itemName: 'Property',
    sections: [
      {
        name: 'General',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software.',
        fields: [
          'Description of Property',
          'Street Address',
          'Address Line 2',
          'City',
          'State/Region',
          'Province',
          'State/Province',
          'Country (if foreign)',
          'Country',
          'Postal Code',
          'ZIP code/Postal Code',
          'Purchase price',
          'Purchase date',
          'Closing Costs',
          'Is the rental property located outside of Canada? If so, please provide the country name, currency, and foreign tax paid',
          "If the rental property is located in Canada, please provide the Purchase price, Purchase date, Closing Costs, and Your Percentage ownership of the rental business (If not 100%, provide Partner's Name, SIN, Partnership business number and Percentage Ownership)",
          'Was this the final year of Rental Operations? If so, please provide the date of final day day of operations',
          'Ownership percentage if less than 100%',
          'What percentage was the property used personally during the year?',
          'Days owned',
          'Days rented at fair market value',
          'Days of personal use (including use by relatives)',
          'Please upload all Form 1098 Mortgage Interest Statement(s) received for this property',
          'Please upload all Form 1098(s) received for this property',
          'Please upload all Form 1098(s) received for this rental property',
          'If the property was purchased, sold, or inherited this year, please upload the final closing document, or appraisal',
          'If the property was purchased, sold, or inherited this year, please upload the closing document, or appraisal',
          'If the property was purchased, sold, or inherited in {questionnaire_year}, please upload the closing document, or appraisal',
          'If the property was refinanced, please upload the refinance final closing document',
          'If the property was refinanced, please upload the refinance closing document',
          'If the property is purchased, sold, or inherited this year, please upload the closing document or appraisal',
          'If the property is purchased, sold, or inherited in {questionnaire_year}, please upload the closing document or appraisal',
          "If you've purchased or inherited this property in 2021, please upload the closing statement or appraisal",
          "If you've purchased or inherited this property in 2022, please upload the closing statement or appraisal",
          "If you've purchased or inherited this property in 2023, please upload the closing statement or appraisal",
          "If you've purchased or inherited this property in {questionnaire_year}, please upload the closing statement or appraisal",
          '<p>Answer yes if you have QuickBooks Online or other online bookkeeping software and have given us access. If you haven&#39;t provided share access yet, please invite us <u>as accountant</u> using the email address <span style="color:#3498db"><u>info@dicpas.com</u></span></p>',
          'Do you use accounting software for tracking your rental income and expenses?',
          'If you use an accounting software not listed above, please provide the name of the software',
        ],
      },
      {
        name: 'Income & Expenses',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software. For Income, include advance rents in the year made. Do not include security deposits that will be returned to the tenant.',
        fields: [
          "Please specify the currency used for your 'Income & Expense' item",
          'Rental Income',
          'Income',
          'Advertising',
          'Alarm',
          'Auto and Travel',
          'Cleaning and Maintenance',
          'Cleaning & maintenance',
          'Commissions',
          'Gardener',
          'Insurance',
          'Interest and Bank Charges',
          'Office Expenses',
          'Legal & professional fees',
          'Legal and Professional Fees',
          'Legal and Professional Services',
          'Management and Administration Fees',
          'Pest Control',
          'Repairs and Maintenance',
          'Salaries, Wages and Benefits',
          'Management Fees',
          'Mortgage Interest',
          'Other Interest',
          'Property Taxes',
          'Travel',
          'Travel (do not include meals and entertainment)',
          'Telephone and Utilities',

          'Motor Vehicle used for Rental',

          'Repairs',
          'Repairs & maintenance',
          'Supplies',
          'Taxes',
          'Utilities',
          'Utilities: Electric',
          'Utilities: Gas',
          'Utilities: Water',

          'Other (include itemized list)',
          'Other (please include category & amount)',
        ],
      },
      {
        name: 'Rental-related Mileage',
        description:
          'Please fill in all fields below for all rental-related use of vehicle if applicable. You must keep a written log showing the date, distance and rental purpose for each trip. If you use multiple vehicles, please label each answer with what vehicle it is for.',
        fields: [
          'For Each Vehicle: Make, Model, and Year of Vehicle',
          'Date Vehicle was placed in service for rental use',
          'Date purchased or leased',
          'Make, Model, and Year of Vehicle',
          'Total miles this vehicle was driven this year, regardless of purpose',
          'Total km/miles this vehicle was driven this year, regardless of purpose',
          'Total rental-related miles driven for the year',
          'Total rental-related km/miles driven for the year',
          'Odometer reading for each vehicle as of 12/31/2021',
          'Odometer reading for each vehicle as of 12/31/2022',
          'Odometer reading for each vehicle as of 12/31/2023',
          'Odometer reading for each vehicle as of 12/31/{questionnaire_year - 1}',
          'Odometer reading for each vehicle as of 12/31/{questionnaire_year}',
          'Total Kilometers this vehicle was driven in the year',
          'Total Rental Kilometers driven in the year',
          'Total rental-related miles driven for each vehicle from January to June',
          'Total rental-related miles driven for each vehicle from July to December',
          'Total miles this vehicle was driven from January to June, regardless of purpose',
          'Total miles this vehicle was driven from July to December, regardless of purpose',
          'Total rental-related miles driven from January to June',
          'Total rental-related miles driven from July to December',
          'Fuel',
          'Vehicle Maintenance and Repair',
          'Vehicle Insurance',
          'Parking',
          'Toll',
          'Vehicle Registration',
          'Vehicle Bill of Sale',
          'Lease or Financing paperwork',
          'Total interest paid in tax year (If you are financing your vehicle)',
          'Date interest started',
          'Date interest ended',
          'Additional notes for vehicle',
        ],
      },
      {
        name: 'New Asset',
        description:
          'Please provide the description(s), cost(s), acquisition date(s) for each asset acquired this year.',
        fields: [
          'If you have a listing of new asset purchases, please upload',
          'Construction Costs',
          'Major Improvements',
          'Furniture',
          'Furniture > $2500 each',
          'Fixtures',
          'Fixtures > $2500 each',
          'Appliances',
          'Landscaping',
          'HVAC',
          'Other (describe)',
          'Other Improvements (describe)',
          'Other Costs (please include category and amount)',
        ],
      },
    ],
  },
  {
    name: 'Rental Property',
    otherNames: ['Rental Property'],
    form: 'Schedule E',
    addName: 'Property',
    itemName: 'Property',
    sections: [
      {
        name: 'General',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software.',
        fields: [
          "If you've purchased or inherited this property in 2021, please upload the closing statement or appraisal",
          "If you've purchased or inherited this property in 2022, please upload the closing statement or appraisal",
          "If you've purchased or inherited this property in 2023, please upload the closing statement or appraisal",
          "If you've purchased or inherited this property in {questionnaire_year}, please upload the closing statement or appraisal",
          'Description of Property',
          'Street Address',
          'Address Line 2',
          'City',
          'State/Region',
          'Province',
          'Country (if foreign)',
          'Postal Code',
          'ZIP code/Postal Code',
          'Purchase price',
          'Purchase date',
          'Closing Costs',
          'Is the rental property located outside of Canada? If so, please provide the country name, currency, and foreign tax paid',
          "If the rental property is located in Canada, please provide the Purchase price, Purchase date, Closing Costs, and Your Percentage ownership of the rental business (If not 100%, provide Partner's Name, SIN, Partnership business number and Percentage Ownership)",
          'Was this the final year of Rental Operations? If so, please provide the date of final day day of operations',
          'Ownership percentage if less than 100%',
          'What percentage was the property used personally during the year?',
          'Days owned',
          'Days rented at fair market value',
          'Days of personal use (including use by relatives)',
          'Do you use accounting software for tracking your rental income and expenses?',
          'If you use an accounting software not listed above, please provide the name of the software',
          'Answer yes if you have QuickBooks Online or other online bookkeeping software and have given us access',
          "Answer yes if you have QuickBooks Online or other online bookkeeping software and have given us access. If you haven't provided share access yet, please click <a href='https://quickbooks.intuit.com/learn-support/en-us/help-article/account-management/managing-accountant-users-quickbooks-online/L2AcdYvHw_US_en_US'>here</a> to learn how to do so.",
          'Please upload QuickBooks Desktop File, P&L Statement, or spreadsheet for bookkeeping if applicable',
        ],
      },
      {
        name: 'Income & Expenses',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software. For Income, include advance rents in the year made. Do not include security deposits that will be returned to the tenant.',
        fields: [
          'Rental Income',
          'Income',

          'Alarm',
          'Advertising',
          'Auto and Travel',
          'Cleaning and Maintenance',
          'Cleaning & maintenance',
          'Commissions',
          'Gardener',
          'Insurance',
          'Interest and Bank Charges',
          'Office Expenses',
          'Legal & professional fees',
          'Legal and Professional Fees',
          'Legal and Professional Services',
          'Management and Administration Fees',
          'Repairs and Maintenance',
          'Salaries, Wages and Benefits',
          'Management Fees',
          'Mortgage Interest',
          'Other Interest',
          'Pest Control',
          'Property Taxes',
          'Travel',
          'Travel (do not include meals and entertainment)',
          'Telephone and Utilities',
          'Utilities: Electric',
          'Utilities: Gas',
          'Utilities: Water',

          'Motor Vehicle used for Rental',
          'Motor vehicle expenses',
          'Repairs',
          'Repairs & maintenance',
          'Supplies',
          'Taxes',
          'Utilities',
          'Heat',
          'Water',
          'Hydro',
          'Other (include itemized list)',
          'Other (please include category & amount)',
          'Other (please include category and amount)',
        ],
      },
      {
        name: 'Rental-related Mileage',
        description:
          'Please fill in all fields below for all rental-related use of vehicle if applicable. You must keep a written log showing the date, distance and rental purpose for each trip. If you use multiple vehicles, please label each answer with what vehicle it is for.',
        fields: [
          'For Each Vehicle: Make, Model, and Year of Vehicle',
          'Date Vehicle was placed in service for rental use',
          'Date purchased or leased',
          'Make, Model, and Year of Vehicle',
          'Total Kilometers this vehicle was driven in the year',
          'Total Rental Kilometers driven in the year',
          'Total rental-related miles driven for each vehicle from January to June',
          'Total rental-related miles driven for each vehicle from July to December',
          'Total miles this vehicle was driven from January to June, regardless of purpose',
          'Total miles this vehicle was driven from July to December, regardless of purpose',
          'Total rental-related miles driven from January to June',
          'Total rental-related miles driven from July to December',
          'Fuel',
          'Vehicle Maintenance and Repair',
          'Vehicle Insurance',
          'Parking',
          'Toll',
          'Vehicle Registration',
          'Vehicle Bill of Sale',
          'Lease or Financing paperwork',
          'Total interest paid in tax year (If you are financing your vehicle)',
          'Date interest started',
          'Date interest ended',
          'Additional notes for vehicle',
        ],
      },
      {
        name: 'Other Costs',
        fields: [
          'Construction Costs',
          'Major Improvements',
          'Furniture',
          'Furniture > $2500 each',
          'Fixtures',
          'Fixtures > $2500 each',
          'Appliances',
          'Landscaping',
          'Major renovations (not repairs)',
          'HVAC',
          'Other (describe)',
          'Other Improvements (describe)',
          'Other Costs (please include category and amount)',
        ],
      },
    ],
  },
  {
    name: 'Business (Schedule C)',
    otherNames: [
      'Business',
      'Business (ビジネス)',
      'Non-Registered Businesses - Single',
      'Non-Registered Businesses - Joint',
    ],
    form: 'Schedule C',
    addName: 'Business',
    itemName: 'Business',
    sections: [
      {
        name: 'General',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software.',
        fields: [
          'Business name',
          'Business name (If registered)',
          'Business address',
          'Business address (If different from home)',
          'Primary business activity',
          'Accounting method',
          'Main product/service of business',
          'Your percentage ownership of the business. If you do not own 100%, please provide Partners Name, SIN, Partnership Business Number, Percentage Ownership.',
          'Owned or operated by',
          'EIN (Employer Identification Number)',
          'Is this business a Single Member LLC?',
          'Name of business owner, both names if owned jointly',
          'Did you make any payments that would require you to file Form(s) 1099?',
          'If you made payments requiring Form(s) 1099, did you or will you file the Form(s) 1099?',
          'Made S-corp election?',
          '<p>Answer yes if you have QuickBooks Online or other online bookkeeping software and have given us access. If you haven&#39;t provided share access yet, please invite us <u>as accountant</u> using the email address <span style="color:#3498db"><u>info@dicpas.com</u></span></p>',
          'Please select a listed accounting software used for tracking your income and expenses?',
          'If you use an accounting software not listed above, please provide the name of the software',
          'If you are using QuickBooks Online. Wave, Xero, or Freshbooks, do we already have access to your file?',
          'Answer yes if you have QuickBooks Online or other online bookkeeping software and have given us access.',
          "Answer yes if you have QuickBooks Online or other online bookkeeping software and have given us access. If you haven't provided share access yet, please click <a href='https://quickbooks.intuit.com/learn-support/en-us/help-article/account-management/managing-accountant-users-quickbooks-online/L2AcdYvHw_US_en_US'>here</a> to learn how to do so.",
          'Please upload QuickBooks Desktop File, P&L Statement, or spreadsheet for bookkeeping if applicable',
          'If you use QuickBooks Desktop or Excel Spreadsheet, please upload Portable QuickBooks Desktop File, P&L Statement, or spreadsheet for bookkeeping.',
        ],
      },
      {
        name: 'Income',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software.',
        fields: [
          "Please specify the currency used for your 'Income & Expense' item",
          'Total Gross Sales',
          'Gross Sales, Commissions or fee (include GST/HST collected or collectible)',
          'GST/HST collected',
          'Total Receipts or Sales',
          'Refunds and Returns',
          'Cost of Goods Sold',
          'Other Income',
          'Other Income (please include source and amount)',
          'Refunds & Returns',
        ],
      },
      {
        name: 'Cost of Goods Sold',
        description: '',
        fields: [
          'Method(s) used to value closing inventory:',
          'Beginning Inventory',
          'Purchases',
          'Labor',
          'Materials',
          'Other Costs',
          'Ending Inventory',
        ],
      },
      {
        name: 'Expenses',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software. Do not put any expense in more than one category.',
        fields: [
          'Purchase of materials',
          'Direct Wage Costs',
          'Subcontract Expenses',
          'Other Business Costs',
          'Meals and Entertainment',
          'Advertising',
          'Bad Debts',
          'Interest and Bank Charges',
          'Business Taxes, Licenses and memberships',
          'Business taxes, licences and memberships',
          'Office Expenses',
          'Office Stationery and Supplies',

          'Accounting',
          'Auto Loan Interest on Vehicle Used for Business Purposes',
          'Business Gifts ($25 max per person per year)',
          'Business and/or Liability Insurance (not life or disability)',
          'Business or Payroll Taxes and Licenses',
          'Business Travel (do not include meals & entertainment)',
          'Business Travel - Lodging, Airfare, Taxi, Auto Rental (not meals & entertainment)',
          'Car and Truck',

          'Auto/Truck - Gas, Insurance',

          'Commissions & Fees Paid to Others',
          'Commissions and Fees Paid to Others',
          'Cost for Continuing Education, Classes, Seminars, etc.',
          'Cost for Continuing Education',
          'Cost for Education',
          'Contract Labor',

          'Depletion',
          'Depreciation and section 179 expense deduction',
          'Equipment and Other Business Assets',
          'Equipment and other Business Assets over $500. Please include Item Name, Purchase Date, and Total Cost.',
          'Equipment and Other Business Assets over $500. <br/><br/>Please input:<ul><li>Item Name</li><li>Purchase Date</li><li>Total Cost</li></ul>',
          'Employee Benefits Programs',
          'Employee Benefit Programs',
          'Health Insurance Premiums',
          'Insurance – Business/Liability/Workers Comp',
          'Interest (not including mortgage interest)',

          'Business and/or Liability Insurance',
          'Interest on Vehicle Loans',
          'Mortgage Interest',
          'Other Interest',
          'Job Supplies (non-inventory)',
          'Legal and Professional',
          'Legal and Professional Services',
          'Management and Administration Fees',
          'Business Rent',
          'Salaries, Wages and Benefits',
          'Business Property Taxes',

          'Meals',
          'Meals – Business and Travel',
          'Mortgage',
          'Office-related',
          'Office Expenses & Supplies',
          'Office Supplies',
          'Parking Fees, Tolls, and Transportation',
          'Parking Fees, Tolls',

          'Pension and Profit-sharing Plans',
          'Type of Plan',

          'Do you wish to calculate an additional contribution based on your Schedule C?',
          'Payroll Tax',
          'Payroll Wages',
          'Professional Memberships',
          'Rent',
          'Rental/Lease of Equipment, Machinery, etc.',
          'Rental/Lease of Office Space, Land, Buildings, etc.',
          'Repairs and Maintenance',
          'Supplies',
          'Supplies (not included in Cost of Goods Sold)',

          'Taxes and Licenses',
          'Licenses',

          'Total Amount Paid for Minor Repairs',
          'Travel',
          'Travel (do not include meals and entertainment)',
          'Business Utilities',
          'Telephone',
          'Telephone (percentage of business use)',
          'Telephone and Internet',
          'Fuel costs (except for motor vehicles)',
          'Motor vehicle expenses',
          'Delivery, freight and express',
          'Delivery, Freight and Postage',
          'Deductible Meals',
          'Postage',
          'Tools',
          'Utilities',
          'Utilities for office rental',

          // Outside Services needs to be right before 1099 questions
          // and right after questions about utilities:
          // https://linear.app/soraban/issue/SOR-4190/operations-request-total-bookkeeping-schedule-c-re-ordering#comment-3390d611
          'Outside Services',
          'Did you issue all required 1099’s?',

          'Wages',
          'Please upload W2’s',
          'Private health service plan (PHSP) premiums',
          'Other (Copywriting)',
          'Other (Dues and subscriptions)',
          'Other (Equipment)',
          'Other (Fees)',
          'Other (Phone)',
          'Other (Postage)',
          'Other (Printing)',
          'Other (Software)',
          'Other (Supplies)',
          'Other (Website)',
          'Other (please include category & amount)',
          'Other (please include category and amount)',
          'Opening Inventory',
          'Closing Inventory',
        ],
      },
      {
        name: 'Home Office',
        description:
          'Please skip if not applicable. The home office must be used regularly & exclusively for business',
        fields: [
          'Is your Home office used regularly and exclusively for business?',
          'Would you like to use Simplified Method ($5 per square foot). If yes, only square footage of home office space is needed',
          'Total Square Footage of the Home',
          'Total rooms in your house including living rooms, kitchen, bedrooms etc. BUT excluding bathrooms',
          'Number of rooms dedicated as Home Office',
          'Square Footage of the Home Office',
          'Square Footage of the Home Office or Inventory Storage',
          'Heat',
          'Electrical',
          'Water',
          'Home Insurance',
          'Maintenance',
          'Rent Paid',
          'Mortgage Interest Paid',
          'Rent',
          'Property Taxes Paid',
          'Property Tax',
          "Home Owner's Insurance",
          'Other Home Office Expense',
          'Total Amount Paid for Utilities',
          'Total Amount Paid or Minor Repairs',
          'Total Amount Paid for Minor Repairs',
          'Major Home Improvements: description, date, and amount for each major home improvement',
          'Total Amount Paid for Major Improvements',

          'Alarm',
          'Gardener',
          'Pest Control',
          'Utilities: Electric',
          'Utilities: Gas',
          'Utilities: Water',
          'Any other home office expenses',

          'Total Square Footage of the Home (if you are claiming Office in Home)',
          'Square Footage of the Home Office (if you are claiming Office in Home)',
          'Total Amount Paid for Major Improvements; describe them and provide amounts for each',
          'Other Home Office Expense (please include category and amount)',
        ],
      },
      {
        name: 'Business Use of Vehicle',
        description:
          'Please fill in all fields below for all business-related use of vehicle if applicable. You must keep a written log showing the date, distance and business purpose for each trip. If you use multiple vehicles, please label each answer with what vehicle it is for.',
        fields: [
          'For Each Vehicle: Make, Model, and Year of Vehicle',
          'For Each Vehicle: Make, Model, and Year of Vehicle and Date placed in service (if new this year)',
          'Date Vehicle was placed in service for business use',
          'Make, Model, and Year of Vehicle',
          'Date purchased or leased',
          'Odometer reading for each vehicle as of 12/31/2021',
          'Odometer reading for each vehicle as of 12/31/2022',
          'Odometer reading for each vehicle as of 12/31/2023',
          'Odometer reading for each vehicle as of 12/31/{questionnaire_year - 1}',
          'Odometer reading for each vehicle as of 12/31/{questionnaire_year}',
          'Total miles this vehicle was driven this year, regardless of purpose',
          'Total km/miles this vehicle was driven this year, regardless of purpose',
          'Total business-related miles driven for the year',
          'Total business-related km/miles driven for the year',
          'Total miles this vehicle was driven from January to June, regardless of purpose',
          'Total business-related miles driven for each vehicle from January to June',
          'Total miles this vehicle was driven from July to December, regardless of purpose',
          'Total business-related miles driven for each vehicle from July to December',
          'Total business-related miles driven from January to June',
          'Total business-related miles driven from July to December',
          'Total Kilometers this vehicle was driven in the year',
          'Total Business Kilometers driven in the year',
          'Date Vehicle was placed in service for business use (if new this year)',
          'Purchase price of vehicle (this is for proprietors that purchased a vehicle during the year that needs to be depreciated)',
          'Fuel',
          'Vehicle Maintenance and Repair',
          'Vehicle Insurance',
          'Vehicle lease payments',
          'Parking',
          'Toll',
          'Vehicle Registration',
          'Vehicle Bill of Sale',
          'Lease or Financing paperwork',
          'Interest paid on auto loan',
          'Total interest paid in tax year (If you are financing your vehicle)',
          'Date interest started',
          'Date interest ended',
          'Additional notes for vehicle',
        ],
      },
      {
        name: 'New Asset',
        description:
          'Please provide the description(s), cost(s), acquisition date(s) for each asset acquired this year.',
        fields: [
          'New Asset #1 (Date, Description, and Amount)',
          'New Asset #2 (Date, Description, and Amount)',
          'New Asset #3 (Date, Description, and Amount)',
          'Other (Date, Description, and Amount)',
          'If you have a listing of new asset purchases, please upload',
          'Construction Costs',
          'Major Improvements',
          'Furniture',
          'Furniture > $2500 each',
          'Fixtures',
          'Fixtures > $2500 each',
          'Appliances',
          'Landscaping',
          'HVAC',
          'Other (describe)',
          'Other Improvements (describe)',
          'Other Costs (please include category and amount)',
        ],
      },
    ],
  },
  {
    name: 'Business Income',
    form: 'Schedule C',
    addName: 'Business',
    otherNames: ['Business Income'],
    itemName: 'Business',
    sections: [
      {
        name: 'General',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software.',
        fields: [
          'Business name (If registered)',
          'Business address',
          'Main product/service',
          'If you do not own 100% of the business, please provide Partners Name, SIN or Partnership Business Number and Percentage Ownership.',
          'GST/HST Number (If applicable)',
          'If you use an accounting software or excel Spreadsheet, please upload it or give access to info@simplifyaccounting.ca',
        ],
      },
      {
        name: 'Income',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software.',
        fields: [
          'Gross Sales or Commissions (include GST/HST collected or collectible)',
          'GST/HST collected',
          'Other Income (please include source and amount)',
        ],
      },
      {
        name: 'Cost of Goods Sold',
        description: '',
        fields: [
          'Method(s) used to value closing inventory:',
          'Beginning Inventory',
          'Purchases',
          'Labor',
          'Materials',
          'Other Costs',
          'Ending Inventory',
        ],
      },
      {
        name: 'Expenses',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software. Do not put any expense in more than one category.',
        fields: [
          'Purchase of materials',
          'Salaries (T4) paid to employees',
          'Subcontractor Expenses',
          'Equipment and Other Business Assets',
          'Meals and Entertainment',
          'Advertising',
          'Bad Debts',
          'Business and/or Liability Insurance',
          'Interest and Bank Charges',
          'Business Taxes, Licenses and memberships',
          'Business taxes, licences and memberships',
          'Office Expenses',
          'Legal and Professional Services',
          'Management and Administration Fees',
          'Repairs and Maintenance',
          'Salaries, Wages and Benefits',
          'Travel (do not include meals and entertainment)',
          'Telephone and Internet',
          'Fuel costs (except for motor vehicles)',
          'Delivery, Freight and Postage',
          'Rental/Lease of Equipment, Machinery, etc.',
          'Rental/Lease of Office Space, Land, Buildings, etc.',
          'Cost for Continuing Education',
          'Tools',
          'Private health service plan (PHSP) premiums',
          'Other (please include category and amount)',
        ],
      },
      {
        name: 'Home Office',
        description:
          'Please skip if not applicable. The home office must be used regularly & exclusively for business',
        fields: [
          'Total home square footage',
          'Dedicated home office square footage',
          'Heat',
          'Electricity',
          'Water',
          'Home Insurance',
          'Maintenance',
          'Mortgage Interest Paid (not principal)',
          'Property Tax',
          'Rent',
          'Other (please include category and amount)',
        ],
      },
      {
        name: 'Business Use of Vehicle',
        description:
          'Please fill in all fields below for all business-related use of vehicle if applicable. You must keep a written log showing the date, distance and business purpose for each trip. If you use multiple vehicles, please label each answer with what vehicle it is for.',
        fields: [
          'Make, Model, and Year of Vehicle',
          'Date purchased or leased',
          'Total Kilometers this vehicle was driven in the year',
          'Total Business Kilometers driven in the year',
          'Fuel',
          'Vehicle Maintenance and Repair',
          'Vehicle Insurance',
          'Vehicle Registration',
          'Vehicle Bill of Sale',
          'Vehicle lease payments',
          'Lease or Financing paperwork',
          'Total interest paid in tax year (If you are financing your vehicle)',
          'Interest paid on auto loan',
          'Purchase price of vehicle (this is for proprietors that purchased a vehicle during the year that needs to be depreciated)',
          'Additional notes for vehicle',
        ],
      },
      {
        name: 'New Asset',
        description:
          'Please provide the description(s), cost(s), acquisition date(s) for each asset acquired this year.',
        fields: [
          'If you have a listing of new asset purchases, please upload',
          'Construction Costs',
          'Major Improvements',
          'Furniture',
          'Furniture > $2500 each',
          'Fixtures',
          'Fixtures > $2500 each',
          'Appliances',
          'Landscaping',
          'HVAC',
          'Other (describe)',
          'Other Improvements (describe)',
          'Other Costs (please include category and amount)',
        ],
      },
    ],
  },
  // Customized Schedule C for Zuazo & Associates (667)
  {
    name: 'Business Activity – Income & Expenses',
    form: 'Schedule C',
    addName: 'Income & Expenses',
    otherNames: ['Business Activity – Income & Expenses'],
    itemName: 'Income & Expenses',
    sections: [
      {
        name: 'Income',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software.',
        fields: [
          'Business Name',
          'Gross Sales or Commissions (include GST/HST collected or collectible)',
          'GST/HST collected',
          'Gross Income (Total Receipts or Sales)',
        ],
      },
      {
        name: 'Cost of Goods Sold',
        description: '',
        fields: ['Purchases', 'Labor', 'Materials', 'Other Costs'],
      },
      {
        name: 'Expenses',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software. Do not put any expense in more than one category.',
        fields: [
          'Purchase of materials',
          'Auto Expenses (or provide Business-Related Mileage)',
          'Salaries (T4) paid to employees',
          'Subcontractor Expenses',
          'Equipment and Other Business Assets',
          'Meals and Entertainment',
          'Advertising',
          'Bad Debts',
          'Business and/or Liability Insurance',
          'Interest and Bank Charges',
          'Business Taxes, Licenses and memberships',
          'Business taxes, licences and memberships',
          'Office Expenses',
          'Legal and Professional Services',
          'Management and Administration Fees',
          'Repairs and Maintenance',
          'Salaries, Wages and Benefits',
          'Travel (do not include meals and entertainment)',
          'Telephone and Internet',
          'Fuel costs (except for motor vehicles)',
          'Delivery, Freight and Postage',
          'Rental/Lease of Equipment, Machinery, etc.',
          'Rental/Lease of Office Space, Land, Buildings, etc.',
          'Cost for Continuing Education',
          'Tools',
          'Private health service plan (PHSP) premiums',
          'Other (please include category and amount)',
        ],
      },
      {
        name: 'Home Office',
        description:
          'Please skip if not applicable. The home office must be used regularly & exclusively for business',
        fields: [
          'Total home square footage',
          'Dedicated home office square footage',
          'Heat',
          'Electricity',
          'Water',
          'Home Insurance',
          'Maintenance',
          'Mortgage Interest Paid (not principal)',
          'Property Tax',
          'Rent',
          'Other (please include category and amount)',
        ],
      },
      {
        name: 'New Asset',
        description:
          'Please provide the description(s), cost(s), acquisition date(s) for each asset acquired this year.',
        fields: [
          'If you have a listing of new asset purchases, please upload',
          'Construction Costs',
          'Major Improvements',
          'Furniture',
          'Furniture > $2500 each',
          'Fixtures',
          'Fixtures > $2500 each',
          'Appliances',
          'Landscaping',
          'HVAC',
          'Other (describe)',
          'Other Improvements (describe)',
          'Other Costs (please include category and amount)',
        ],
      },
    ],
  },
  // Customized Schedule C for Steven M. Vogt, CPA (790)
  {
    name: 'Businesses Activities',
    otherNames: ['Businesses Activities'],
    form: 'Schedule C',
    addName: 'Business',
    itemName: 'Business',
    sections: [
      {
        name: 'General',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software.',
        fields: [
          'Business name',
          'Business address',
          'Type of business',
          'EIN (Employer Identification Number)',
          'Made S-corp election?',
          'If you use QuickBooks Desktop or Excel Spreadsheet, please upload Portable QuickBooks Desktop File, P&L Statement, or spreadsheet for bookkeeping.',
        ],
      },
      {
        name: 'Income',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software.',
        fields: [
          'Total Receipts or Sales - including income received on tax forms',
          'Refunds and Returns',
          'Other Income (please include source and amount)',
          'Please upload all Form 1099(s) received',
        ],
      },
      {
        name: 'Cost of Goods Sold',
        description: '',
        fields: [
          'Beginning Inventory',
          'Purchases',
          'Labor',
          'Materials',
          'Other Costs',
          'Ending Inventory',
        ],
      },
      {
        name: 'Expenses',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software. Do not put any expense in more than one category.',
        fields: [
          'Advertising',
          'Commissions and Fees Paid to Others',
          'Continuing Education',
          'Contract Labor',
          'Equipment and Other Business Assets - under $500/item',
          'Equipment and Other Business Assets - more than $500/item',
          'Employee Benefit Programs - provide details',
          'Business and/or Liability Insurance',
          'Mortgage Interest',
          'Other Interest',
          'Legal and Professional Services',
          'Office Expenses and Supplies',
          'Parking Fees, Tolls, and Transportation',
          'Pension and Profit-sharing Plans',
          'Dues/Subscriptions',
          'Rental/Lease of Equipment, Machinery, etc.',
          'Rental/Lease of Office Space, Land, Buildings, etc.',
          'Repairs and Maintenance',
          'Shipping/Postage',
          'Supplies (not included in Cost of Goods Sold)',
          'Taxes and Licenses',
          'Travel (do not include meals and entertainment)',
          'Deductible Meals',
          'Cellphone (business use only)',
          'Internet Service Provider',
          'Utilities',
          'Wages',
          'Total Amount Paid for Minor Repairs',
          'Other (Explain)',
        ],
      },
      {
        name: 'Home Office',
        description:
          'Please skip if not applicable. The home office must be used regularly & exclusively for business',
        fields: [
          'Would you like to use Simplified Method ($5 per square foot). If yes, only square footage of home office space is needed',
          'Total Square Footage of the Home',
          'Square Footage of the Home Office',
          'if your business is an in-home daycare, please answer the following entries:',
          'Rent Paid',
          'Mortgage Interest Paid',
          'Property Taxes Paid',
          "Home Owner's Insurance",
          'Home Owner Association Dues',
          'Total Amount Paid for Utilities',
          'Total Amount Paid for Minor Repairs',
          'Major Home Improvements: description, date, and amount for each major home improvement',
        ],
      },
      {
        name: 'Business Use of Vehicle',
        description:
          'Please fill in all fields below for all business-related use of vehicle if applicable. You must keep a written log showing the date, distance and business purpose for each trip. If you use multiple vehicles, please label each answer with what vehicle it is for.',
        fields: [
          'Date Vehicle was placed in service for business use',
          'Year, Make, Model of Vehicle',
          'Total miles this vehicle was driven this year, regardless of purpose',
          'Total business-related miles driven for the year',
          'Vehicle License Tax paid during the year',
          'Loan interest paid on this vehicle during the year',
          'Additional notes for vehicle',
        ],
      },
    ],
  },
  // Customized Schedule E for Zuazo & Associates (667)
  {
    name: 'Rental Activity - Income & Expenses',
    otherNames: ['Rental Activity - Income & Expenses'],
    form: 'Schedule E',
    addName: 'Income & Expenses',
    itemName: 'Income & Expenses',
    sections: [
      {
        name: 'Income & Expenses',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software. For Income, include advance rents in the year made. Do not include security deposits that will be returned to the tenant.',
        fields: [
          'Street Address of Rental Property',
          'Rental Income',
          'Income',
          'Alarm',
          'Advertising',
          'Auto and Travel (or provide Rental-Related Mileage)',
          'Cleaning and Maintenance',
          'Cleaning & maintenance',
          'Commissions',
          'Gardener',
          'Insurance',
          'Interest and Bank Charges',
          'Office Expenses',
          'Legal & professional fees',
          'Legal and Professional Fees',
          'Legal and Professional Services',
          'Management and Administration Fees',
          'Repairs and Maintenance',
          'Salaries, Wages and Benefits',
          'Management Fees',
          'Mortgage Interest',
          'Other Interest',
          'Pest Control',
          'Property Taxes',
          'Travel',
          'Travel (do not include meals and entertainment)',
          'Telephone and Utilities',
          'Utilities: Electric',
          'Utilities: Gas',
          'Utilities: Water',
          'Motor Vehicle used for Rental',
          'Motor vehicle expenses',
          'Repairs',
          'Repairs & maintenance',
          'Supplies',
          'Taxes',
          'Utilities',
          'Heat',
          'Water',
          'Hydro',
          'Other (include itemized list)',
          'Other (please include category & amount)',
          'Other (please include category and amount)',
        ],
      },
      {
        name: 'Other Costs',
        fields: [
          'Construction Costs',
          'Major Improvements',
          'Furniture',
          'Furniture > $2500 each',
          'Fixtures',
          'Fixtures > $2500 each',
          'Appliances',
          'Landscaping',
          'Major renovations (not repairs)',
          'HVAC',
          'Other (describe)',
          'Other Improvements (describe)',
          'Other Costs (please include category and amount)',
        ],
      },
    ],
  },
  // Customized Schedule E for Steven M. Vogt, CPA (790)
  {
    name: 'Rental Activities',
    otherNames: ['Rental Activities'],
    form: 'Schedule E',
    addName: 'Property',
    itemName: 'Property',
    sections: [
      {
        name: 'General',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software.',
        fields: [
          'Description of Property',
          'Street Address',
          'Address Line 2',
          'City',
          'State/Region',
          'State/Province',
          'Country (if foreign)',
          'ZIP code/Postal Code',
          'Ownership percentage if less than 100%',
          'Number of days available to rent this year',
          'Days of personal use (including use by relatives)',
          'If the property was purchased, sold, or inherited this year, please upload the closing document, or appraisal',
          'Please upload all Form 1098(s) received for this rental property',
          'If the property was refinanced, please upload the refinance closing document',
          'If the property was purchased or refinanced during the year, what is the length of the loan?',
        ],
      },
      {
        name: 'Income & Expenses',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software. For Income, include advance rents in the year made. Do not include security deposits that will be returned to the tenant.',
        fields: [
          'Income',
          'Advertising',
          'Cleaning and Maintenance',
          'Cleaning & maintenance',
          'Commissions',
          'Gardener',
          'Insurance',
          "Homeowner's Association Dues",

          'Legal and Professional Fees',

          'Management Fees',
          'Mortgage Interest',
          'Other Interest',

          'Repairs - do NOT include capital improvements and asset purchases over $500',

          'Supplies',
          'Real Estate Taxes',
          'Utilities',
        ],
      },
      {
        name: 'Rental-related Mileage',
        description:
          'Please fill in all fields below for all rental-related use of vehicle if applicable. You must keep a written log showing the date, distance and rental purpose for each trip. If you use multiple vehicles, please label each answer with what vehicle it is for.',
        fields: [
          'Date Vehicle was placed in service for rental use',

          'Year, Make, and Model of Vehicle',
          'Total miles this vehicle was driven this year, regardless of purpose',

          'Total rental-related miles driven for the year',
          'Vehicle License Tax paid during the year',
          'Loan interest paid on this vehicle during the year',

          'Additional notes for vehicle or other travel expenses for this rental property',
        ],
      },
      {
        name: 'Capital Improvements and New Asset',
        description:
          'Please provide the description(s), cost(s), acquisition date(s) for each asset acquired this year.',
        fields: [
          'Construction Costs',
          'Furniture & Fixtures',
          'Appliances',
          'Landscaping',
          'HVAC',
          'Other (describe)',
        ],
      },
    ],
  },
  {
    name: 'Farm (Schedule F)',
    otherNames: ['Farm', 'Farm (農業)', 'Farm Income'],
    form: 'Schedule F',
    addName: 'Farming Activity',
    itemName: 'Farming Activity',
    sections: [
      {
        name: 'General',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software.',
        fields: [
          'Principal crop or activity',
          'Description',
          'Accounting method',
          'Owned or operated by',
          'EIN (Employer Identification Number)',
          'Did you make any payments that would require you to file Form(s) 1099?',
          'If you made payments requiring Form 1099(s), did you or will you file the Form(s) 1099',
          'If you made payments requiring Form(s) 1099, did you or will you file the Form(s) 1099?',
          'Answer yes if you have QuickBooks Online or other online bookkeeping software and have given us access',
          "Answer yes if you have QuickBooks Online or other online bookkeeping software and have given us access. If you haven't provided share access yet, please click <a href='https://quickbooks.intuit.com/learn-support/en-us/help-article/account-management/managing-accountant-users-quickbooks-online/L2AcdYvHw_US_en_US'>here</a> to learn how to do so.",
          'Please upload QuickBooks Desktop File, P&L Statement, or spreadsheet for bookkeeping if applicable',
        ],
      },
      {
        name: 'Income',
        fields: [
          'Sales of purchased livestock and other resale items',
          'Cost or other basis of purchased livestock or other items',
          'Sales of livestock, produce, grains, and other products you raised',
          'Total Cooperative Distributions',
          'Taxable Cooperative Distributions',
          'Total Agricultural Program Payments',
          'Taxable Agricultural Program Payments',
          'Commodity Credit Corporation (CCC) Loans',
          'Total Crop Insurance Proceeds and Federal Crop Disaster Payments',
          'Taxable Crop Insurance Proceeds and Federal Crop Disaster Payments',
          'Mark if electing to defer crop insurance proceeds to next tax year',
          'Total CCC loans forfeited',
          'Taxable CCC loans forfeited',
          'Custom Hire (machine work) Income',
          'Other Income (please include source and amount)',
        ],
      },

      {
        name: 'Expenses',
        description: 'Do not put any expense in more than one category.',
        fields: [
          'Car and Truck',
          'Chemicals',
          'Conservation',
          'Custom Labor',
          'Employee Benefit Programs',
          'Feed',
          'Fertilizers and Lime ',
          'Freight and Trucking',
          'Gasoline, Fuel, and Oil',
          'Mortgage Interest',
          'Other Interest',
          'Business and/or Liability Insurance',
          'Labor Hired',
          'Pension and Profit-sharing Plans',
          'Rental/Lease of Equipment, Machinery, etc.',
          'Rental/Lease of Land, Animals, etc.',
          'Repairs and Maintenance',
          'Seeds and Plants',
          'Storage and warehousing',
          'Supplies',
          'Taxes',
          'Utilities',
          'Veterinary, Breeding, and Medicine',
          'Other (please include category & amount)',
        ],
      },
      {
        name: 'Business Use of Vehicle',
        description:
          'Please fill in all fields below for all business use of vehicle if applicable. You must have a business location or home office in order to claim business mileage. You must keep a written mileage log showing the date, miles and business purpose for each trip. The IRS does not allow a deduction for undocumentated mileage.\nIf you use multiple vehicles, please label each answer with what vehicle it is for.',
        fields: [
          'Date Vehicle was placed in service for business use',
          'Make, Model, and Year of Vehicle',
          'Total miles this vehicle was driven this year, regardless of purpose',
          'Total farming-related miles driven for the year',
          'Total miles this vehicle was driven from January to June, regardless of purpose',
          'Total miles this vehicle was driven from July to December, regardless of purpose',
          'Total farming-related miles driven from January to June',
          'Total farming-related miles driven from July to December',
          'Additional notes for vehicle',
        ],
      },
      {
        name: 'New Asset',
        description:
          'Please provide the description(s), cost(s), acquisition date(s) for each asset acquired this year.',
        fields: [
          'If you have a listing of new asset purchases, please upload',
          'Construction Costs',
          'Major Improvements',
          'Furniture',
          'Furniture > $2500 each',
          'Fixtures',
          'Fixtures > $2500 each',
          'Appliances',
          'Landscaping',
          'HVAC',
          'Other (describe)',
          'Other Improvements (describe)',
          'Other Costs (please include category and amount)',
        ],
      },
    ],
  },
  // Customized Schedule F for Steven M. Vogt, CPA (790)
  {
    name: 'Farm Activities',
    otherNames: ['Farm Activities'],
    form: 'Schedule F',
    addName: 'Farming Activity',
    itemName: 'Farming Activity',
    sections: [
      {
        name: 'General',
        description:
          'Only fill out Income & Expense sections if you have not provided P&L, bookkeeping spreadsheet, or access to your bookkeeping software.',
        fields: [
          'Principal crop or activity',
          'Description',
          'Accounting method',
        ],
      },
      {
        name: 'Income',
        fields: [
          'Sales of purchased livestock and other resale items',
          'Cost or other basis of purchased livestock or other items',
          'Sales of livestock, produce, grains, and other products you raised',
          'Total Cooperative Distributions',
          'Taxable Cooperative Distributions',
          'Total Agricultural Program Payments',
          'Taxable Agricultural Program Payments',
          'Commodity Credit Corporation (CCC) Loans',
          'Total Crop Insurance Proceeds and Federal Crop Disaster Payments',
          'Taxable Crop Insurance Proceeds and Federal Crop Disaster Payments',
          'Mark if electing to defer crop insurance proceeds to next tax year',
          'Total CCC loans forfeited',
          'Taxable CCC loans forfeited',
          'Custom Hire (machine work) Income',
          'Other Income (please include source and amount)',
        ],
      },

      {
        name: 'Expenses',
        description: 'Do not put any expense in more than one category.',
        fields: [
          'Chemicals',
          'Conservation',
          'Custom Labor',
          'Employee Benefit Programs',
          'Feed',
          'Fertilizers and Lime ',
          'Freight and Trucking',
          'Gasoline, Fuel, and Oil',
          'Mortgage Interest',
          'Other Interest',
          'Business and/or Liability Insurance',
          'Labor Hired',
          'Pension and Profit-sharing Plans',
          'Rental/Lease of Equipment, Machinery, etc.',
          'Rental/Lease of Land, Animals, etc.',
          'Repairs and Maintenance',
          'Seeds and Plants',
          'Storage and warehousing',
          'Supplies',
          'Taxes',
          'Utilities',
          'Veterinary, Breeding, and Medicine',
        ],
      },
      {
        name: 'Business Use of Vehicle',
        description:
          'Please fill in all fields below for all business use of vehicle if applicable. You must have a business location or home office in order to claim business mileage. You must keep a written mileage log showing the date, miles and business purpose for each trip. The IRS does not allow a deduction for undocumentated mileage.\nIf you use multiple vehicles, please label each answer with what vehicle it is for.',
        fields: [
          'Date Vehicle was placed in service for business use',
          'Year, Make and Model of Vehicle',
          'Total miles this vehicle was driven this year, regardless of purpose',
          'Total farming-related miles driven for the year',
          'Vehicle License Tax paid during the year',
          'Loan interest paid on this vehicle during the year',
          'Additional notes for vehicle',
        ],
      },
      {
        name: 'New Asset',
        description:
          'Please provide the description(s), cost(s), acquisition date(s) for each asset acquired this year.',
        fields: [],
      },
    ],
  },
  {
    name: 'S-Corps and Partnerships',
    otherNames: ['S-Corp and Partnership'],
    addName: 'Existing Business',
    itemName: 'Business',
    customTabHeader:
      'Please fill in for existing business (in all applicable tabs below)',
    sections: [
      {
        name: 'General',
        fields: [
          'Business name',
          'EIN (Employer Identification Number)',
          'Changed Corporate Address',
          'Had Changes in Ownership',
          'Changed Bank Information',
        ],
      },
      {
        name: 'Financial Information',
        description:
          "If you don't have QuickBooks or other financial statements, please fill out the Income and Expense tabs",
        fields: [
          'Make a selection regarding payments that require Form 1099s to be filed.',
          'Answer yes if you have QuickBooks Online or other online bookkeeping.',
          "Answer yes if you have QuickBooks Online or other online bookkeeping software and have given us access. If you haven't provided share access yet, please click <a href='https://quickbooks.intuit.com/learn-support/en-us/help-article/account-management/managing-accountant-users-quickbooks-online/L2AcdYvHw_US_en_US'>here</a> to learn how to do so.",
          'Are your QuickBooks Online up to date and ready for tax?',
          'If no, please upload QuickBooks Desktop file, P&L Statement or spreadsheet or complete the Income and Expenses on the “Income” and “Expense” tab.',
          "Provide the business's bank account(s) cash balance at 12/31",
        ],
      },
      {
        name: 'Income',
        description:
          "Fill out the Income and Expense tabs if you don't have QuickBooks or other financial statements",
        fields: [
          'Income',
          'Gross Income not included in 1099 (Incentives, Prizes, Virtual Currency)',
          'If you prefer to upload documentation or statements, upload files here.',
        ],
      },
      {
        name: 'Expense',
        description:
          "Fill out the Income and Expense tabs if you don't have QuickBooks or other financial statements",
        fields: [
          'Advertising (flyers, other, etc.)',
          'Auto Expense (actual costs; fuel, oil, repairs, registration, etc)',
          'Bank Fees (monthly checking, overdraft, ATM, etc.)',
          'Business Insurance (General Liability not auto or health)',
          'Commissions (paid to recruits, or other sub-contractors) )',
          'Sales Expense (Pay customer or pay for installation costs)',
          'Interest (credit card, business lines, or other loans)',
          'Interest (Auto Loan Interest)',
          'Internet',
          'Legal & Professional (attorney or accountant, etc.)',
          'Licenses & Permits (business, solicitors, or other)',
          'Meal (business related)',
          'Outside services (pay for services from other independent contractor)',
          'Officer Salary (CPA Input)',
          'Officer Health Insurance',
          'Payroll Taxes (CPA Input)',
          'Postage',
          'Rent (away from home apartment if sold in another state temporarily)',
          'Recruiting Expense (non reimbursed meals)',
          'Referral Fees (paid to customers or anyone else)',
          'Supplies (Office supplies, protective gear, etc.)',
          'Telephone (Mobile, fax line, etc)',
          'Travel (Airfare, hotels, rental cars, tolls, parking)',
          'Utilities (out of state apartment if applicable)',
          'Wages (employees other than owners)',
        ],
      },
      {
        name: 'Payroll',
        fields: [
          'Make a selection regarding Payroll',
          'Please upload W-3, owner & officer W-2s. Shareholder/Partner health insurance premiums (if applicable)',
        ],
      },
      {
        name: 'Auto',
        description:
          'Auto Expenses: Daily mileage logs are required to substantiate all auto expenses deductions. Personal vehicles used for business require written accountable plan, reimbursement worksheets, and actual reimbursement.',
        fields: [
          'How many miles did you drive for business?',
          'How many miles did you drive total for year?',
          'Did you travel for business away from home overnight.',
          'If applicable what cities and states did you stay overnight?',
          'If applicable list beginning and return date for overnight stays.',
          'If you prefer to upload documentation or statements, upload files here.',
        ],
      },
      {
        name: 'Loans / Asset Purchases & Sales',
        fields: [
          'Please list all new loans or debts by the company in 2022, if not already provided to your DrillDown bookkeeper. (if applicable)',
          'Please upload all loan or debt documents listed above (including ones regarding loan or debt forgiveness), (if applicable).',
          'Please list all new business vehicles or equipment purchases or sales by the company in 2022, if not already provided to your DrillDown bookkeeper. (if applicable)',
          'Please upload all purchase or sale documents listed above, (if applicable).',
        ],
      },
      {
        name: 'K-1 Investments',
        fields: [
          'Does the company have any K-1 investments in 2022? (in an S-Corporation or Partnership)',
          'If applicable, Please, upload K-1s (if not prepared by DrillDown).',
        ],
      },
      {
        name: 'Rental Activity',
        fields: [
          'Please upload rental property income and expense,(P&L,  mortgage statements, and tax statements) owned by the company during 2022, if applicable.',
          'Did your company purchase or sell real estate for 2022?',
          'Upload purchase or sale settlement statements (if applicable).',
        ],
      },
      {
        name: 'Estimated Tax Payments',
        description: 'Please list all estimated tax payments here.',
        fields: [
          'Please upload all payment verification documents.',
          ' #1',
          ' #2',
          ' #3',
          ' #4',
          'Please list all other payments here (Which state, date paid, amount paid)',
        ],
        groupQuestions: true,
      },
      {
        name: 'Home Office',
        description:
          'Please skip if not applicable. The home office must be used regularly & exclusively for business',
        fields: [
          'Total Square Footage of the Home',
          'Square Footage of the Home Office',
          'Square Footage of the Home Office or Inventory Storage',
          'Rent Paid',
          'Mortgage Interest Paid',
          'Property Taxes Paid',
          "Home Owner's Insurance",
          'Total Amount Paid for Utilities',
          'Total Amount Paid or Minor Repairs',
          'Total Amount Paid for Minor Repairs',
          'Total Amount Paid for Major Improvements',
        ],
      },
    ],
  },
];

// NOTE @chris Should be an actual enum later; readonly Record<string, string>
export const statesRecord = Object.freeze({
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'District of Columbia',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
});

export const states = Object.values(statesRecord);
export const stateKeys = Object.keys(statesRecord);

export const timeZones = [
  'America/Adak',
  'Hawaii',
  'Alaska',
  'America/Anchorage',
  'America/Metlakatla',
  'America/Nome',
  'America/Sitka',
  'America/Yakutat',
  'Pacific Time (US & Canada)',
  'America/Boise',
  'Arizona',
  'Mountain Time (US & Canada)',
  'America/Indiana/Knox',
  'America/Indiana/Tell_City',
  'America/Menominee',
  'America/North_Dakota/Beulah',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'Central Time (US & Canada)',
  'America/Detroit',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'Eastern Time (US & Canada)',
  'Indiana (East)',
];

export const defaultEntityTypes = [
  'Individual',
  'Partnership',
  'Trust',
  'Exempt Organization',
];

export const FILING_STATUSES = [
  {
    label: 'Single',
    value: 's',
  },
  {
    label: 'Married Filing Jointly',
    value: 'mfj',
  },
  {
    label: 'Married Filing Separately',
    value: 'mfs',
  },
  {
    label: 'Head of Household',
    value: 'hoh',
  },
  {
    label: 'Qualifying Widow(er)',
    value: 'qw',
  },
];

export const templateFrequencyOptions = [
  {
    text: 'Once',
    value: 'once',
  },
  {
    text: 'Weekly',
    value: 'weekly',
  },
  {
    text: 'Monthly (specific day)',
    value: 'monthly',
  },
  {
    text: 'Last business day of month',
    value: 'month_end',
  },
  {
    text: 'First business day of month',
    value: 'month_start',
  },
  {
    text: 'Quarterly',
    value: 'quarterly',
  },
  {
    text: 'Yearly',
    value: 'yearly',
  },
];

export const navigationTiles = {
  taxDashboardTile: ({ companyId, hashId, clientName }) => ({
    title: 'Dashboard',
    name: 'dashboard',
    path: hashId ? `/${clientName}-${hashId}` : `/?companyId=${companyId}`,
    icon: 'dashboard',
  }),
  taxBookkeepingTile: {
    title: 'Bookkeeping',
    path: '/bookkeeping',
    icon: 'import_contacts',
  },
  reviewAndExportTile: ({ companyId, hashId, clientName }) => ({
    id: 'review-export-tile',
    title: 'Review & Export',
    name: 'review-export',
    path: hashId
      ? `/review-export/${clientName}-${hashId}`
      : `/review-export?companyId=${companyId}`,
    icon: 'computer',
  }),
  documentsTile: ({ companyId, hashId, clientName }) => ({
    id: 'documents-tile',
    title: 'Documents',
    name: 'admin-documents',
    path: hashId
      ? `/admin-documents/${clientName}-${hashId}`
      : `/admin-documents?companyId=${companyId}`,
    icon: 'description',
  }),
  accountsTile: {
    title: 'Accounts',
    path: '/accounts',
    icon: 'account_balance_wallet',
  },
  conversationTile: ({ companyId, hashId, clientName }) => ({
    id: 'conversation-tile',
    title: 'Conversation',
    path: hashId
      ? `/conversation/${clientName}-${hashId}`
      : `/conversation?companyId=${companyId}`,
    name: 'conversation',
    icon: 'insert_comment',
  }),
  reviewBookkeepingTile: {
    title: 'Bookkeeping',
    path: '/review-bookkeeping',
    icon: 'import_contacts',
  },
  assistantTile: {
    title: 'Assistant',
    path: '/assistant',
    icon: 'help_outline',
  },
  reportsTile: {
    title: 'Reports',
    path: '/reports',
    icon: 'summarize',
  },
  adminDashboardTile: ({ companyId, hashId, clientName }) => ({
    title: 'Dashboard',
    path: hashId
      ? `/admin-dashboard/${clientName}-${hashId}`
      : `/admin-dashboard?companyId=${companyId}`,
    name: 'admin-dashboard',
    icon: 'dashboard',
  }),
  adminDraftTile: {
    title: 'Draft',
    path: '/review-admin-draft',
    icon: 'note',
  },
};

export const emailFormatter = value => {
  if (!value) return value;
  let parts = value.split('@');
  if (parts.length !== 2) return value;
  parts[1] = parts[1].toLowerCase();
  return parts.join('@');
};

export const accountMenuTiles = {
  overviewMenuTile: {
    title: 'Overview',
    path: '/overview',
    icon: 'border_all',
    shortcut: 'Ctrl/Cmd + h',
  },
  profileMenuTile: {
    title: 'Profile',
    path: '/admin-profile',
    icon: 'account_circle',
    shortcut: 'p',
  },
  timesheetMenuTile: {
    title: 'Timesheets',
    path: '/timesheet',
    icon: 'access_time',
    shortcut: 't',
  },
  manageFirmMenuTile: {
    id: 'manage-firm',
    title: 'Manage Firm',
    path: '/firm-settings',
    icon: 'corporate_fare',
  },
  taskBoardMenuTile: {
    title: 'Task Board',
    path: '/task-board',
    icon: 'space_dashboard',
  },
  suggestedTasksMenuTile: {
    title: 'Suggested Tasks',
    path: '/suggested-tasks',
    icon: 'format_list_bulleted',
  },
  todoListMenuTile: {
    title: 'Todo Tasks',
    path: '/todo-tasks',
    icon: 'format_list_bulleted',
  },
  notificationsSummaryMenuTile: {
    title: 'Activity Summary',
    path: '/summary',
    icon: 'event_note',
  },
  myCloudMenuTile: {
    title: 'My Cloud',
    path: '/cloud',
    icon: 'filter_drama',
  },
  reviewAndExportMenuTile: ({ companyId, hashId, clientName }) => ({
    title: 'Review & Export',
    path: hashId
      ? `/review-export/${clientName}-${hashId}`
      : `/review-export?companyId=${companyId}`,
    icon: 'computer',
  }),
  dashboardMenuTile: ({ companyId, hashId, clientName }) => ({
    title: 'Dashboard',
    name: 'dashboard',
    path: hashId ? `/${clientName}-${hashId}` : `/?companyId=${companyId}`,
    icon: 'dashboard',
  }),
  settingsMenuTile: ({ companyId, hashId, clientName }) => ({
    title: 'Settings',
    name: 'settings',
    path: hashId
      ? `/settings/${clientName}-${hashId}`
      : `/settings?companyId=${companyId}`,
    icon: 'settings',
  }),
};

export const supportedExtensionsForPreview = [
  'docx',
  'pptx',
  'xlsx',
  'xls',
  'doc',
  'png',
  'jpeg',
  'jpg',
  'gif',
  'tiff',
  'pdf',
  'heic',
];

export const predefinedQuestionAnswers = [
  'Not Applicable',
  'Marked Completed',
  'Provided in Questionnaire',
  'Provided Elsewhere',
  'Recognized in Bulk Document',
];

/**
 * Supported countries for the application. In each object,
 * - `text`: ISO 3166 name
 * - `value`: ISO 3166 alpha-2 code
 */
export const otherCountries = getData()
  .map(({ code, name }) => ({ value: code, text: name }))
  .sort((a, b) => a.text.localeCompare(b.text));

export const rules = {
  presence: [v => !!v || 'This field is required'],
  email: [
    v => !!v || 'E-mail is required',
    v => /.+@.+/.test(v) || 'E-mail must be valid',
  ],
  dueDate: [
    v => moment() < moment(v) || !v || 'Due date must be in the future',
  ],
  sendDate: [v => moment() < moment(v) || 'Send date must be in the future'],
  positiveNumber: [
    v => Number.isInteger(parseFloat(v)) || !v || 'Must be an integer',
    v => v >= 0 || 'Must be a positive integer',
  ],
};

export const yearOptions = [
  moment().year() + 1,
  moment().year(),
  moment().year() - 1,
  moment().year() - 2,
  moment().year() - 3,
  moment().year() - 4,
  moment().year() - 5,
  moment().year() - 6,
  moment().year() - 7,
  moment().year() - 8,
];

export const taskStatus = {
  Todo: 0,
  Completed: 1,
  Dismissed: 2,
};

export const taskPriority = {
  Low: 0,
  Medium: 1,
  High: 2,
  Urgent: 3,
};

export const usTaxForms = [
  // income
  '1040',
  '1040-SR',
  '1040-NR',
  '1040-X',
  'W-2',
  'W-2G',
  '1099-INT',
  '1099-DIV',
  '1099-MISC',
  '1099-NEC',
  '1099-G',
  '1099-R',
  '1099-B',
  '1099-K',
  'Schedule B (1040)',
  'Schedule C (1040)',
  'Schedule D (1040)',
  'Schedule E (1040)',
  'Schedule F (1040)',
  'Schedule K-1',

  // credits and deductions
  'Schedule A (1040)',
  '2441',
  '8863',
  '8880',
  '8889',
  '5695',
  '8396',
  '1040-ES',

  // employment taxes
  '941',
  '940',
  'W-4',
  'W-9',
  '944',
  '945',

  // estate and gift taxes
  '706',
  '709',

  // excise taxes
  '720',
  '2290',

  // business taxes
  '1065',
  '1120',
  '1120-S',
  '990',

  // international
  '3520',
  '3520-A',
  '5471',
  '8865',
  '8938',

  // miscellaneous
  '4506',
  '4506-T',
  '8822',
  '8829',
];
